import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { usePin } from '../context/PinContext';
import { useTheme } from '../context/ThemeContext';
import api from '../lib/api';
import { requestPersistentStorage } from '../lib/localDb';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import { Sun, Moon, Download, Shield, Lock, Palette, Upload, Database, AlertTriangle, Eye, EyeOff, Trash2, KeyRound, Hash, MessageSquare } from 'lucide-react';
import { FeedbackDialog } from '../components/FeedbackDialog';
import { toast } from 'sonner';

export default function SettingsPage() {
  const navigate = useNavigate();
  const { lock, changePasscode, wipeAllAppData, passcodeType } = usePin();
  const { theme, toggleTheme } = useTheme();
  const [exporting, setExporting] = useState(false);
  const [exportingBackup, setExportingBackup] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  // true = browser will keep our data, false = it may clear it, null = unknown/unsupported
  const [storagePersisted, setStoragePersisted] = useState(null);

  useEffect(() => {
    requestPersistentStorage().then(setStoragePersisted);
  }, []);

  // Change Passcode state
  const [changePasscodeOpen, setChangePasscodeOpen] = useState(false);
  const [currentPasscode, setCurrentPasscode] = useState('');
  const [newPasscode, setNewPasscode] = useState('');
  const [confirmNewPasscode, setConfirmNewPasscode] = useState('');
  const [newPasscodeType, setNewPasscodeType] = useState(passcodeType || 'pin');
  const [showCurrentPasscode, setShowCurrentPasscode] = useState(false);
  const [showNewPasscode, setShowNewPasscode] = useState(false);
  const [showConfirmPasscode, setShowConfirmPasscode] = useState(false);
  const [changingPasscode, setChangingPasscode] = useState(false);
  const [passcodeError, setPasscodeError] = useState('');

  // Reset/Wipe state
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [wipeConfirmation, setWipeConfirmation] = useState('');
  const [wiping, setWiping] = useState(false);

  const handleLock = () => {
    lock();
    navigate('/unlock');
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const response = await api.get('/export/csv', { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `arms_room_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Data exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  // Export full backup (ZIP)
  const handleExportBackup = async () => {
    setExportingBackup(true);
    try {
      const response = await api.get('/export/backup');
      const blob = response.data;
      
      // Validate we got a real blob
      if (!(blob instanceof Blob) || blob.size === 0) {
        throw new Error('Export returned empty or invalid data');
      }
      
      // Generate filename with timestamp
      const filename = `arms_room_backup_${new Date().toISOString().split('T')[0]}.zip`;
      
      // Use the most reliable download method
      // Method 1: Use the download attribute with a proper blob URL
      const url = window.URL.createObjectURL(blob);
      
      // Create and configure the download link
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      
      // Append to body (required for Firefox)
      document.body.appendChild(link);
      
      // Trigger download
      link.click();
      
      // Cleanup after a short delay to ensure download starts
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      toast.success(`Backup created: ${filename}`);
    } catch (error) {
      console.error('Backup error:', error);
      toast.error('Failed to create backup: ' + (error.message || 'Unknown error'));
    } finally {
      setExportingBackup(false);
    }
  };

  // Import backup (ZIP)
  const handleImportBackup = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.endsWith('.zip')) {
      toast.error('Please select a valid backup file (.zip)');
      return;
    }

    setImporting(true);
    try {
      const response = await api.post('/import/backup', { 
        file: file, 
        mode: 'replace' 
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        // Refresh the page to load new data
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        toast.error(response.data.message || 'Import failed');
      }
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Failed to import backup');
    } finally {
      setImporting(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Get minimum length based on new passcode type
  const newMinLength = newPasscodeType === 'password' ? 6 : 4;

  // Handle Change Passcode
  const handleChangePasscode = async () => {
    setPasscodeError('');

    // Validate current passcode
    if (currentPasscode.length < 4) {
      setPasscodeError('Current passcode must be at least 4 characters');
      return;
    }

    // Validate new passcode based on type
    if (newPasscodeType === 'pin') {
      if (!/^\d{4,8}$/.test(newPasscode)) {
        setPasscodeError('New PIN must be 4-8 digits');
        return;
      }
    } else {
      if (newPasscode.length < 6) {
        setPasscodeError('New password must be at least 6 characters');
        return;
      }
    }

    // Validate confirmation
    if (newPasscode !== confirmNewPasscode) {
      setPasscodeError(`New ${newPasscodeType === 'pin' ? 'PINs' : 'passwords'} do not match`);
      return;
    }

    setChangingPasscode(true);
    try {
      const result = await changePasscode(currentPasscode, newPasscode, newPasscodeType);
      if (result.success) {
        toast.success(`${newPasscodeType === 'pin' ? 'PIN' : 'Password'} changed successfully`);
        setChangePasscodeOpen(false);
        resetPasscodeForm();
      } else {
        setPasscodeError(result.error || 'Current passcode is incorrect');
      }
    } catch (error) {
      setPasscodeError('Failed to change passcode');
    } finally {
      setChangingPasscode(false);
    }
  };

  const resetPasscodeForm = () => {
    setCurrentPasscode('');
    setNewPasscode('');
    setConfirmNewPasscode('');
    setNewPasscodeType(passcodeType || 'pin');
    setShowCurrentPasscode(false);
    setShowNewPasscode(false);
    setShowConfirmPasscode(false);
    setPasscodeError('');
  };

  const handleNewPasscodeChange = (e) => {
    let value = e.target.value;
    if (newPasscodeType === 'pin') {
      value = value.replace(/\D/g, '').slice(0, 8);
    }
    setNewPasscode(value);
  };

  const handleConfirmNewPasscodeChange = (e) => {
    let value = e.target.value;
    if (newPasscodeType === 'pin') {
      value = value.replace(/\D/g, '').slice(0, 8);
    }
    setConfirmNewPasscode(value);
  };

  // Handle Reset App - wipe all data with proper async handling
  const handleResetApp = async () => {
    // Validate confirmation text
    if (wipeConfirmation !== 'WIPE') {
      return;
    }
    
    // Prevent double-click
    if (wiping) {
      return;
    }
    
    setWiping(true);
    try {
      // Perform the full data wipe (async)
      await wipeAllAppData();
      
      // Close dialog and reset state
      setResetDialogOpen(false);
      setWipeConfirmation('');
      
      // Show success message
      toast.success('All data has been erased. Please set up a new passcode.');
      
      // Navigate to passcode setup
      navigate('/setup-pin', { replace: true });
    } catch (error) {
      console.error('[Settings] Wipe error:', error);
      toast.error('Failed to wipe data. Please try again.');
      setWiping(false);
    }
  };

  const resetWipeDialog = () => {
    setWipeConfirmation('');
    setResetDialogOpen(false);
  };

  return (
    <Layout title="Settings">
      <div className="max-w-2xl space-y-6" data-testid="settings-page">
        {/* Passcode & Security */}
        <Card className="bg-card border-border rounded-sm">
          <CardHeader className="border-b border-border/50 p-4">
            <CardTitle className="text-sm uppercase tracking-widest font-['Chivo'] flex items-center gap-2">
              <Lock className="w-4 h-4" />
              Passcode & Security
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-foreground">Lock App</h3>
                <p className="text-sm text-muted-foreground">
                  Lock the app immediately. You will need your passcode to unlock.
                </p>
              </div>
              <Button
                onClick={handleLock}
                variant="outline"
                className="rounded-sm"
                data-testid="lock-app-settings-btn"
              >
                <Lock className="w-4 h-4 mr-2" />
                Lock Now
              </Button>
            </div>

            <Separator />

            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-foreground">Change Passcode</h3>
                <p className="text-sm text-muted-foreground">
                  Update your {passcodeType === 'password' ? 'password' : 'PIN'} or switch between PIN and password.
                </p>
              </div>
              <AlertDialog open={changePasscodeOpen} onOpenChange={(open) => {
                setChangePasscodeOpen(open);
                if (!open) resetPasscodeForm();
              }}>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    className="rounded-sm"
                    data-testid="change-pin-btn"
                  >
                    <KeyRound className="w-4 h-4 mr-2" />
                    Change Passcode
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="sm:max-w-md">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Change Passcode</AlertDialogTitle>
                    <AlertDialogDescription>
                      Enter your current passcode and choose a new PIN or password.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  
                  <div className="space-y-4 py-4">
                    {/* Current Passcode */}
                    <div className="space-y-2">
                      <Label htmlFor="current-passcode">Current Passcode</Label>
                      <div className="relative">
                        <Input
                          id="current-passcode"
                          type={showCurrentPasscode ? 'text' : 'password'}
                          value={currentPasscode}
                          onChange={(e) => {
                            setCurrentPasscode(e.target.value);
                            setPasscodeError('');
                          }}
                          placeholder="Enter current passcode"
                          className="pr-10 text-center font-mono tracking-widest"
                          data-testid="current-pin-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowCurrentPasscode(!showCurrentPasscode)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showCurrentPasscode ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    {/* New Passcode Type Toggle */}
                    <div className="space-y-2">
                      <Label>New Passcode Type</Label>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant={newPasscodeType === 'pin' ? 'default' : 'outline'}
                          size="sm"
                          className={`flex-1 ${newPasscodeType === 'pin' ? 'bg-secondary text-secondary-foreground' : ''}`}
                          onClick={() => {
                            setNewPasscodeType('pin');
                            setNewPasscode('');
                            setConfirmNewPasscode('');
                          }}
                        >
                          <Hash className="w-4 h-4 mr-2" />
                          PIN
                        </Button>
                        <Button
                          type="button"
                          variant={newPasscodeType === 'password' ? 'default' : 'outline'}
                          size="sm"
                          className={`flex-1 ${newPasscodeType === 'password' ? 'bg-secondary text-secondary-foreground' : ''}`}
                          onClick={() => {
                            setNewPasscodeType('password');
                            setNewPasscode('');
                            setConfirmNewPasscode('');
                          }}
                        >
                          <KeyRound className="w-4 h-4 mr-2" />
                          Password
                        </Button>
                      </div>
                    </div>

                    {/* New Passcode */}
                    <div className="space-y-2">
                      <Label htmlFor="new-passcode">
                        New {newPasscodeType === 'pin' ? 'PIN (4-8 digits)' : 'Password (6+ characters)'}
                      </Label>
                      <div className="relative">
                        <Input
                          id="new-passcode"
                          type={showNewPasscode ? 'text' : 'password'}
                          value={newPasscode}
                          onChange={handleNewPasscodeChange}
                          placeholder={newPasscodeType === 'pin' ? 'Enter new PIN' : 'Enter new password'}
                          className="pr-10 text-center font-mono tracking-widest"
                          inputMode={newPasscodeType === 'pin' ? 'numeric' : 'text'}
                          data-testid="new-pin-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPasscode(!showNewPasscode)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showNewPasscode ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    {/* Confirm New Passcode */}
                    <div className="space-y-2">
                      <Label htmlFor="confirm-new-passcode">
                        Confirm New {newPasscodeType === 'pin' ? 'PIN' : 'Password'}
                      </Label>
                      <div className="relative">
                        <Input
                          id="confirm-new-passcode"
                          type={showConfirmPasscode ? 'text' : 'password'}
                          value={confirmNewPasscode}
                          onChange={handleConfirmNewPasscodeChange}
                          placeholder={newPasscodeType === 'pin' ? 'Confirm new PIN' : 'Confirm new password'}
                          className="pr-10 text-center font-mono tracking-widest"
                          inputMode={newPasscodeType === 'pin' ? 'numeric' : 'text'}
                          data-testid="confirm-new-pin-input"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPasscode(!showConfirmPasscode)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showConfirmPasscode ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    {/* Error */}
                    {passcodeError && (
                      <div className="flex items-center gap-2 text-destructive text-sm">
                        <AlertTriangle className="w-4 h-4" />
                        {passcodeError}
                      </div>
                    )}
                  </div>

                  <AlertDialogFooter>
                    <AlertDialogCancel onClick={resetPasscodeForm}>Cancel</AlertDialogCancel>
                    <Button
                      onClick={handleChangePasscode}
                      disabled={changingPasscode || currentPasscode.length < 4 || newPasscode.length < newMinLength || newPasscode !== confirmNewPasscode}
                      data-testid="confirm-change-pin-btn"
                    >
                      {changingPasscode ? 'Changing...' : 'Change Passcode'}
                    </Button>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>

            <Separator />

            {/* Forgot Passcode Warning */}
            <div className="bg-destructive/10 border border-destructive/30 rounded-sm p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-destructive mb-1">Forgot Passcode?</p>
                  <p className="text-muted-foreground">
                    There is no way to recover a forgotten passcode. Resetting will 
                    <strong className="text-destructive"> permanently erase all local data</strong>. 
                    This includes all firearms, ammunition, accessories, documents, and wishlist items.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Theme */}
        <Card className="bg-card border-border rounded-sm">
          <CardHeader className="border-b border-border/50 p-4">
            <CardTitle className="text-sm uppercase tracking-widest font-['Chivo'] flex items-center gap-2">
              <Palette className="w-4 h-4" />
              Appearance
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {theme === 'dark' ? (
                  <Moon className="w-5 h-5 text-muted-foreground" />
                ) : (
                  <Sun className="w-5 h-5 text-accent" />
                )}
                <div>
                  <Label htmlFor="theme-toggle" className="text-base font-medium">
                    Dark Mode
                  </Label>
                  <p className="text-sm text-muted-foreground">
                    {theme === 'dark' ? 'Dark theme is active' : 'Light theme is active'}
                  </p>
                </div>
              </div>
              <Switch
                id="theme-toggle"
                checked={theme === 'dark'}
                onCheckedChange={toggleTheme}
                data-testid="theme-toggle"
              />
            </div>
          </CardContent>
        </Card>

        {/* Backup & Restore - Always show for local PIN-locked app */}
        <Card className="bg-card border-border rounded-sm">
          <CardHeader className="border-b border-border/50 p-4">
            <CardTitle className="text-sm uppercase tracking-widest font-['Chivo'] flex items-center gap-2">
              <Database className="w-4 h-4" />
              Backup & Restore
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div className="bg-muted/30 rounded-sm p-3 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
              <div className="text-sm text-muted-foreground">
                <p className="font-medium text-foreground mb-1">Local Storage</p>
                <p>Your data is stored locally in your browser. Create backups regularly to prevent data loss. Backups include all inventory items, documents, and images.</p>
                {storagePersisted === true && (
                  <p className="mt-2 text-foreground" data-testid="storage-persist-status">Storage protection: on. The browser won't clear your data on its own.</p>
                )}
                {storagePersisted === false && (
                  <p className="mt-2 text-destructive" data-testid="storage-persist-status">Storage protection: off. The browser may clear your data if space runs low or the app goes unused. Add Arms Room to your home screen (or install it) to turn protection on, and keep regular backups.</p>
                )}
              </div>
            </div>
            
            <Separator />
            
            <div className="space-y-3">
              <div>
                <h4 className="text-sm font-medium mb-2">Export Backup</h4>
                <p className="text-sm text-muted-foreground mb-3">
                  Download a complete backup of all your data as a ZIP file.
                </p>
                <Button
                  onClick={handleExportBackup}
                  disabled={exportingBackup}
                  variant="outline"
                  className="rounded-sm"
                  data-testid="export-backup-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {exportingBackup ? 'Creating Backup...' : 'Export Backup (.zip)'}
                </Button>
              </div>
              
              <Separator />
              
              <div>
                <h4 className="text-sm font-medium mb-2">Import Backup</h4>
                <p className="text-sm text-muted-foreground mb-3">
                  Restore data from a previously exported backup file. 
                  <span className="text-destructive font-medium"> Warning: This will replace all existing data.</span>
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".zip"
                  onChange={handleImportBackup}
                  className="hidden"
                  id="backup-file-input"
                  data-testid="import-backup-input"
                />
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={importing}
                  variant="outline"
                  className="rounded-sm"
                  data-testid="import-backup-btn"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {importing ? 'Importing...' : 'Import Backup (.zip)'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Export CSV */}
        <Card className="bg-card border-border rounded-sm">
          <CardHeader className="border-b border-border/50 p-4">
            <CardTitle className="text-sm uppercase tracking-widest font-['Chivo'] flex items-center gap-2">
              <Download className="w-4 h-4" />
              Data Export
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <p className="text-muted-foreground mb-4">
              Export your inventory data as a CSV file for spreadsheet applications. 
              This includes firearms, ammunition, accessories, and wishlist items (without images).
            </p>
            <Button
              onClick={handleExportCSV}
              disabled={exporting}
              variant="outline"
              className="rounded-sm"
              data-testid="export-csv-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              {exporting ? 'Exporting...' : 'Export as CSV'}
            </Button>
          </CardContent>
        </Card>

        {/* Feedback */}
        <Card className="bg-card border-border rounded-sm">
          <CardHeader className="border-b border-border/50 p-4">
            <CardTitle className="text-sm uppercase tracking-widest font-['Chivo'] flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              Feedback
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <p className="text-muted-foreground mb-4">
              Found a bug or have an idea? Send it straight to the developer.
              Only your message is sent, never your inventory.
            </p>
            <FeedbackDialog />
          </CardContent>
        </Card>

        {/* Reset App - Danger Zone */}
        <Card className="bg-card border-destructive/50 rounded-sm">
          <CardHeader className="border-b border-destructive/30 p-4">
            <CardTitle className="text-sm uppercase tracking-widest font-['Chivo'] flex items-center gap-2 text-destructive">
              <AlertTriangle className="w-4 h-4" />
              Danger Zone
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-medium text-foreground">Reset / Wipe All Local Data</h3>
                <p className="text-sm text-muted-foreground">
                  Permanently delete all data and reset your PIN.
                </p>
              </div>
              <AlertDialog open={resetDialogOpen} onOpenChange={(open) => {
                setResetDialogOpen(open);
                if (!open) setWipeConfirmation('');
              }}>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="destructive"
                    className="rounded-sm"
                    data-testid="reset-app-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Wipe Data
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle className="flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-destructive" />
                      Wipe All Data?
                    </AlertDialogTitle>
                    <AlertDialogDescription className="space-y-3">
                      <p>
                        This will <strong className="text-destructive">permanently erase all your data</strong>, including:
                      </p>
                      <ul className="list-disc list-inside text-sm space-y-1">
                        <li>All firearms and their images</li>
                        <li>All ammunition records</li>
                        <li>All accessories</li>
                        <li>All documents</li>
                        <li>All wishlist items</li>
                        <li>Your PIN</li>
                      </ul>
                      <div className="pt-2">
                        <p className="font-medium mb-2">
                          Type <span className="font-mono bg-muted px-2 py-0.5 rounded text-destructive">WIPE</span> to confirm:
                        </p>
                        <Input
                          value={wipeConfirmation}
                          onChange={(e) => setWipeConfirmation(e.target.value.toUpperCase())}
                          placeholder="Type WIPE to confirm"
                          className="font-mono text-center"
                          data-testid="wipe-confirmation-input"
                        />
                      </div>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel onClick={resetWipeDialog} disabled={wiping}>Cancel</AlertDialogCancel>
                    <Button
                      onClick={handleResetApp}
                      disabled={wipeConfirmation !== 'WIPE' || wiping}
                      variant="destructive"
                      data-testid="confirm-reset-app-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      {wiping ? 'Erasing...' : 'Erase All Data'}
                    </Button>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </CardContent>
        </Card>

        {/* Disclaimer */}
        <Card className="bg-card border-border rounded-sm">
          <CardHeader className="border-b border-border/50 p-4">
            <CardTitle className="text-sm uppercase tracking-widest font-['Chivo'] flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Safety & Legal
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-4">
            <div className="bg-muted/50 rounded-sm p-4 text-sm text-muted-foreground">
              <p className="font-medium text-foreground mb-2">Important Notice</p>
              <p>
                This app is for personal inventory and record-keeping only. Users are responsible 
                for complying with all local, state, and federal laws regarding firearm ownership, 
                storage, and documentation.
              </p>
            </div>
            <Separator />
            <div className="text-sm text-muted-foreground">
              <p className="font-medium text-foreground mb-2">About Arms Room</p>
              <p>
                A professional-grade inventory management system for responsible firearm owners.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
